import { buildUomRows, buildActualsMap, parseTraxxallDate } from '../components/TraxxallDueList'
import { FLEET_UTILIZATION, FLEET_REGS } from './fleetUtilization'

const HOURS_CYCLE_UOMS = new Set(['Hrs', 'Ldg', 'Enc', 'Prc', 'Ach', 'Flcy'])

// Returns { [reg]: Map<item_object, { projDate: Date|null, projType: string|null }> }
// Only fleet aircraft (in FLEET_REGS) are projected; others are omitted entirely so
// TraxxallDueList falls back to Traxxall's own time.rem. approximates for those aircraft.
export function buildAircraftProjections(allCalData) {
  const result = {}

  for (const [reg, d] of Object.entries(allCalData)) {
    if (!d?.items?.length) continue
    if (!FLEET_REGS.has(reg)) continue

    const util        = FLEET_UTILIZATION[reg]
    const actualsMap  = buildActualsMap(d.actuals || [])
    const projMap     = new Map()

    for (const item of d.items) {
      const rows        = buildUomRows(item, actualsMap)
      const remaining   = {}        // { [uom]: hrs/cycles remaining to hard due }
      const tolOvdUoms  = new Set() // UOMs where the hard limit is already exhausted (in tolerance)

      for (const r of rows) {
        if (!r.uom || r.isApprox) continue
        if (r.tolRem != null) {
          // Hard limit already exhausted — item is past due and living on tolerance.
          // Tolerance is a buffer only; project as past-due now rather than extending
          // the projection horizon by the remaining tolerance window.
          tolOvdUoms.add(r.uom)
          continue
        }
        const rem = String(r.rem || '').trim()
        if (!rem || rem === 'OVD') continue
        const num = parseFloat(rem.replace(/,/g, ''))
        if (isNaN(num) || num <= 0) continue
        // Tolerance is not added — project to when the hard due is reached (not when tolerance exhausts)
        if (!(r.uom in remaining)) remaining[r.uom] = num
      }

      // Build projection candidates for each dimension we have utilization rates for
      const candidates = []

      // For tol-ovd UOMs, use the item's actual hard calendar due date so callouts land
      // on the day tolerance was entered, not on today.
      const calDue = parseTraxxallDate(item['next Due Date'])
      for (const uom of tolOvdUoms) {
        candidates.push({ date: calDue, type: uom })
      }

      if (remaining['Hrs'] != null && util.hrsPerMonth > 0)
        candidates.push({ date: byRate(remaining['Hrs'], util.hrsPerMonth), type: 'Hrs' })

      if (remaining['Ldg'] != null && util.ldgPerMonth > 0)
        candidates.push({ date: byRate(remaining['Ldg'], util.ldgPerMonth), type: 'Cycles' })

      if (remaining['Enc'] != null && util.encPerMonth > 0)
        candidates.push({ date: byRate(remaining['Enc'], util.encPerMonth), type: 'Cycles' })

      // Ach tracks with airframe flight hours — use hrsPerMonth directly.
      if (remaining['Ach'] != null && util.hrsPerMonth > 0)
        candidates.push({ date: byRate(remaining['Ach'], util.hrsPerMonth), type: 'Hrs' })

      // Prc / Flcy — use engine cycles as the closest available proxy.
      for (const uom of ['Prc', 'Flcy']) {
        if (remaining[uom] != null && util.encPerMonth > 0)
          candidates.push({ date: byRate(remaining[uom], util.encPerMonth), type: 'Cycles' })
      }

      const valid = candidates.filter(c => c.date !== null)
      if (valid.length === 0) {
        projMap.set(item, { projDate: null, projType: null })
        continue
      }

      // Pick whichever dimension exhausts soonest
      valid.sort((a, b) => a.date - b.date)
      const { date: projDate, type: projType } = valid[0]
      projMap.set(item, { projDate, projType })
    }

    result[reg] = projMap
  }

  return result
}

// Returns { [reg]: Map<item_object, { projDate: Date|null, projType: string|null }> }
// Projects the date when each in-tolerance item's tolerance window will be exhausted by utilization.
export function buildToleranceExpiryProjections(allCalData) {
  const result = {}

  for (const [reg, d] of Object.entries(allCalData)) {
    if (!d?.items?.length) continue
    if (!FLEET_REGS.has(reg)) continue

    const util       = FLEET_UTILIZATION[reg]
    const actualsMap = buildActualsMap(d.actuals || [])
    const projMap    = new Map()

    for (const item of d.items) {
      const rows       = buildUomRows(item, actualsMap)
      const candidates = []

      for (const r of rows) {
        if (r.tolRem == null || r.isApprox) continue
        if (!HOURS_CYCLE_UOMS.has(r.uom)) continue  // calendar tolerance shown via soft-overdue path

        const num = parseFloat(String(r.tolRem).replace(/,/g, ''))
        if (isNaN(num) || num <= 0) continue

        let rate = null
        if (r.uom === 'Hrs' || r.uom === 'Ach')          rate = util?.hrsPerMonth
        else if (r.uom === 'Ldg')                          rate = util?.ldgPerMonth
        else if (['Enc', 'Prc', 'Flcy'].includes(r.uom))  rate = util?.encPerMonth

        if (rate > 0) candidates.push({ date: byRate(num, rate), type: r.uom })
      }

      if (!candidates.length) continue
      candidates.sort((a, b) => a.date - b.date)
      const { date: projDate, type: projType } = candidates[0]
      projMap.set(item, { projDate, projType })
    }

    if (projMap.size > 0) result[reg] = projMap
  }

  return result
}

// Project a date using a constant monthly utilization rate.
// remaining / ratePerMonth = months until exhaustion.
function byRate(remaining, ratePerMonth) {
  if (!remaining || !ratePerMonth || ratePerMonth <= 0) return null
  const months = remaining / ratePerMonth
  return new Date(Date.now() + months * 30.44 * 86_400_000)
}
